/**
 * @file Abstract base class for insight
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { OnInit } from '@angular/core';
import { AbstractComponent } from '../../core/helpers/abstract-component';
import { InsightProvider } from '../insight-provider';
import { InsightService } from '../../insights/insight.service';
import { LocalisationService } from '../../core/localisation.service';
import * as moment from 'moment';
import { EntityChangedSubscription } from '../helpers/entity-changed-subscription';
import { EntityAction } from 'breeze-client';
import { map } from 'rxjs/operators';
import { ResourceService } from '../../resource.service';
import { AlertsService } from '../../core/alerts.service';
/**
 * Base class for an insight component.
 */
var InsightBase = /** @class */ (function (_super) {
    tslib_1.__extends(InsightBase, _super);
    /**
     * Constructs a BaseInsight.
     * @param configurationsService A service that manages the configuration for components.
     * @param provider A provider that contains extra information such as configuration data.
     */
    function InsightBase(resourceService, provider, insightService, localisationService, alertsService) {
        var _this = _super.call(this, alertsService) || this;
        _this.resourceService = resourceService;
        _this.provider = provider;
        _this.insightService = insightService;
        _this.localisationService = localisationService;
        _this.alertsService = alertsService;
        // Data object for all insights.
        _this.data = {};
        // State of the insight
        _this.state = 'loading';
        // Width and height of content part of insight
        _this.insightContentWidth = 248;
        _this.insightContentHeight = 170;
        return _this;
    }
    /**
     * Register component by the insight provider
     */
    InsightBase.register = function () {
        console.warn('The insight MUST register itself');
    };
    /**
     * Is called directly after the creation of this component.
     */
    InsightBase.prototype.ngOnInit = function () {
        this.configuration = this.provider.container.childComponent.configuration;
    };
    /**
     * Get the state of the insight
     * @returns Returns the state of the insight
     */
    InsightBase.prototype.getState = function () {
        return this.state;
    };
    /**
     * Set the state of the insight
     * @params newState The new state of the insight
     */
    InsightBase.prototype.setState = function (newState) {
        this.state = newState;
    };
    /**
     * Check if insight is in given state
     * @params state The state
     * @returns Returns true if given state is equal to state of insight
     */
    InsightBase.prototype.inState = function (state) {
        return (this.state === state);
    };
    /**
    * Used to correct rounding errors in the interface
    * @returns The floor of the value
    */
    InsightBase.prototype.floor = function (value) {
        return Math.floor(value);
    };
    /**
    * Normalize the percentage (> 0 and < 101)
    * @returns The floor of the value
    */
    InsightBase.prototype.limitPercentage = function (value) {
        if (!value || typeof value !== 'number' || value < 0) {
            return 0;
        }
        if (value > 100) {
            return 100;
        }
        return value;
    };
    Object.defineProperty(InsightBase.prototype, "userResourceKey", {
        /**
         * The resource Oid
         * @returns The resource Oid as string
         */
        get: function () {
            return this.resourceService.getResourceOid();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Transforms hours into duration
     * @param value The minutes in 3.25 notation (3 minutes and 15 seconds)
     * @returns The duration as string, for example 3:15
     */
    InsightBase.prototype.toDuration = function (value) {
        var hours = Math.floor(Math.abs(value) / 60);
        var minutes = Math.floor(Math.abs(value) % 60);
        var duration = hours + ':' + minutes;
        if (minutes < 10) {
            duration = duration + '0';
        }
        if (value < 0) {
            duration = '-' + duration;
        }
        return duration;
    };
    /**
     * Is called after the configuration is loaded.
     */
    InsightBase.prototype.afterInitialize = function () {
        _super.prototype.afterInitialize.call(this);
        // Save the header and footer key in the data object
        this.data['headerKey'] = this.configuration.data.headerKey;
        this.data['footerKey'] = this.configuration.data.footerKey;
    };
    /**
     * Use this method when the insight must update when the horizon date changes.
     * @see Constructor InsightAvailableHoursComponent for an example.
     * @protected
     * @param {SubscriptionPool} pool Locale subscription pool to cleanup subscriptions on destroy
     * @param {Router} router Is used to get changes in horizon dates.
     * @param {Function} handler The function that handles the change in horizon date voor specific insight component.
     */
    InsightBase.prototype.makeHorizonDateListener = function (pool, router, handler) {
        var current = moment().startOf('day');
        var level = 'week';
        handler(current, level);
        this.subscriptionPool.add(router.routerState.root.queryParams.pipe(map(function (params) {
            var prms = JSON.parse(JSON.stringify(params).toLowerCase());
            if (prms['date'] && prms['levelname']) {
                return {
                    date: moment(prms['date'], 'YYYYMMDD'),
                    level: prms['levelname']
                };
            }
            return null;
        })).subscribe(function (value) {
            if (value && value['date'] && (!current.isSame(value['date']) || level !== value['level'])) {
                current = value['date'];
                level = value['level'];
                handler(current, level);
            }
        }));
    };
    InsightBase.prototype.makeEntityChangedListener = function (pool, breezeService, handler) {
        var entityChangedSubscription = new EntityChangedSubscription(breezeService.entityManager, breezeService.entityManager.entityChanged.subscribe(function (data) {
            if (data.entityAction !== EntityAction.MergeOnSave &&
                data.entityAction !== EntityAction.Detach) {
                return;
            }
            handler(data);
        }));
        pool.add(entityChangedSubscription);
    };
    return InsightBase;
}(AbstractComponent));
export { InsightBase };
